"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { toast } from "sonner";

import {
  useCreateMockTestMutation,
  useUploadMockTestImageMutation,
} from "@/features/api/mockTestApi";

/* ================= MEDIA HELPER ================= */

const resolveMediaUrl = (url = "") => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/uploads")) return `https://fetc.in${url}`;
  return url;
};

/* ================= SLUG GENERATOR ================= */

const generateSlug = (text = "") =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

export default function CreateMockTestPage() {

  const navigate = useNavigate();

  const [createMockTest, { isLoading }] = useCreateMockTestMutation();
  const [uploadImage] = useUploadMockTestImageMutation();

  const [slugEdited, setSlugEdited] = useState(false);

  const [input, setInput] = useState({
    name: "",
    slug: "",
    price: "",
    description: "",
    image: "",
    status: "draft",
  });

  const [imagePreview, setImagePreview] = useState(null);

  /* ================= INPUT CHANGE ================= */

  const handleChange = (e) => {

    const { id, value } = e.target;

    setInput((prev) => {

      if (id === "name" && !slugEdited) {
        return {
          ...prev,
          name: value,
          slug: generateSlug(value),
        };
      }

      if (id === "slug") setSlugEdited(true);

      return {
        ...prev,
        [id]: value,
      };

    });

  };

  /* ================= IMAGE UPLOAD ================= */

  const handleImageUpload = async (e) => {

    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      return toast.error("Only image allowed");
    }

    const formData = new FormData();
    formData.append("file", file);

    try {

      const res = await uploadImage(formData).unwrap();

      const url = res.fileUrl || res.url;

      setInput((prev) => ({
        ...prev,
        image: url,
      }));

      setImagePreview(resolveMediaUrl(url));

      toast.success("Image uploaded");

    } catch {

      toast.error("Upload failed");

    }

  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!input.name.trim()) {
      return toast.error("Name required");
    }

    const payload = {
      name: input.name.trim(),
      slug: input.slug || generateSlug(input.name),
      description: input.description || "",
      price: Number(input.price) || 0,
      image: input.image || "",
      status: input.status,
    };

    try {

      await createMockTest(payload).unwrap();

      toast.success("Mock test created");

      navigate("/admin/mock-tests");

    } catch (err) {

      toast.error(err?.data?.message || "Create failed");

    }

  };

  return (

    <div className="p-6 max-w-xl space-y-6">

      <h1 className="text-2xl font-bold">
        Create Mock Test
      </h1>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* NAME */}

        <div>
          <Label>Name</Label>
          <Input
            id="name"
            value={input.name}
            onChange={handleChange}
            placeholder="Mock Test Name"
          />
        </div>

        {/* SLUG */}

        <div>
          <Label>Slug</Label>
          <Input
            id="slug"
            value={input.slug}
            onChange={handleChange}
            placeholder="auto-generated"
          />
        </div>

        {/* PRICE */}

        <div>
          <Label>Price (₹)</Label>
          <Input
            id="price"
            type="number"
            value={input.price}
            onChange={handleChange}
            placeholder="0 for free"
          />
        </div>

        {/* STATUS */}

        <div>
          <Label>Status</Label>

          <select
            id="status"
            value={input.status}
            onChange={handleChange}
            className="w-full border rounded-md p-2"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>

        </div>

        {/* DESCRIPTION */}

        <div>
          <Label>Description</Label>
          <Textarea
            id="description"
            rows={4}
            value={input.description}
            onChange={handleChange}
            placeholder="Mock test description"
          />
        </div>

        {/* IMAGE */}

        <div className="space-y-2">

          <Label>Image</Label>

          <Input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
          />

          {imagePreview && (

            <div className="flex items-center gap-3 mt-2">

              <img
                src={imagePreview}
                alt="Preview"
                className="w-32 h-20 rounded border object-cover"
              />

              <Button
                type="button"
                variant="destructive"
                size="icon"
                onClick={() => {
                  setImagePreview(null);
                  setInput((p) => ({ ...p, image: "" }));
                }}
              >
                ✕
              </Button>

            </div>

          )}

        </div>

        {/* ACTIONS */}

        <div className="flex gap-3">

          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate("/admin/mock-tests")}
            className="flex-1"
          >
            Back
          </Button>

          <Button
            type="submit"
            className="flex-1"
            disabled={isLoading}
          >

            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Mock Test"
            )}

          </Button>

        </div>

      </form>

    </div>

  );
}
